import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react'
import type { MemoryColorField, MemoryLinkType, MemoryNetworkData, MemoryNetworkNode, MemoryRow } from './types'
import { hashString, timestampValue } from './utils'

interface PreparedNode extends MemoryNetworkNode {
  wx: number
  wy: number
  degree: number
  heat: number
}

interface PreparedLink {
  source: number
  target: number
  type: MemoryLinkType
}

interface AmbientSelection {
  hubs: number[]
}

interface Props {
  data: MemoryNetworkData
  variant: 'constellation' | 'graph'
  visibleLinkTypes: Set<MemoryLinkType>
  colorField: MemoryColorField
  maxNodes?: number
  showLabels: boolean
  selectedId?: string | null
  highlightedIds?: string[]
  ariaLabel: string
  onSelect: (row: MemoryRow) => void
  onClear: () => void
}

export interface MemoryNetworkCanvasHandle {
  imageFile: () => Promise<File | null>
}

const MAX_DRAWN_LINKS = 6_000
const MAX_HIGHLIGHTED_LINKS = 1_200
const VIEW_MARGIN = 60
// Rótulos enxutos: no zoom padrão a constelação é uma rede de pontos, não uma
// parede de etiquetas. Só entram quando o usuário se aproxima (ou quando o nó
// está sob o cursor/em foco), e mesmo aí ficam curtos e estreitos.
const LABEL_ZOOM_THRESHOLD = 0.9
const LABEL_MAX_CHARS = 28
const LABEL_MAX_WIDTH = 160
const LINK_COLOR_PROPERTIES: Record<MemoryLinkType, string> = {
  semantic: '--memory-link-semantic',
  temporal: '--memory-link-temporal',
  entity: '--memory-link-entity',
  causal: '--memory-link-causal',
}

function resolveCanvasPalette(canvas: HTMLCanvasElement) {
  const styles = getComputedStyle(canvas)
  const fallback = styles.getPropertyValue('--brand').trim() || styles.color
  return {
    focus: styles.getPropertyValue('--memory-focus').trim() || fallback,
    links: Object.fromEntries(
      Object.entries(LINK_COLOR_PROPERTIES).map(([type, property]) => [
        type,
        styles.getPropertyValue(property).trim() || fallback,
      ]),
    ) as Record<MemoryLinkType, string>,
  }
}

function lerp(current: number, target: number) {
  return current + (target - current) * 0.14
}

function heatColor(value: number) {
  const stops = [[56, 130, 220], [170, 130, 200], [240, 100, 60]]
  const normalized = Math.max(0, Math.min(1, value))
  const segment = normalized * (stops.length - 1)
  const index = Math.min(Math.floor(segment), stops.length - 2)
  const progress = segment - index
  const from = stops[index]
  const to = stops[index + 1]
  return `rgb(${Math.round(from[0] + (to[0] - from[0]) * progress)},${Math.round(from[1] + (to[1] - from[1]) * progress)},${Math.round(from[2] + (to[2] - from[2]) * progress)})`
}

const MemoryNetworkCanvas = forwardRef<MemoryNetworkCanvasHandle, Props>(function MemoryNetworkCanvas({
  data,
  variant,
  visibleLinkTypes,
  colorField,
  maxNodes,
  showLabels,
  selectedId,
  highlightedIds = [],
  ariaLabel,
  onSelect,
  onClear,
}, forwardedRef) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const ambientCanvasRef = useRef<HTMLCanvasElement>(null)
  const frameRef = useRef<number | null>(null)
  const ambientFrameRef = useRef<number | null>(null)
  const drawRef = useRef<() => void>(() => undefined)
  const onSelectRef = useRef(onSelect)
  const onClearRef = useRef(onClear)
  const selectedIdRef = useRef(selectedId ?? null)
  const positionsRef = useRef(new Map<string, { wx: number; wy: number }>())
  const preparedRef = useRef<{ nodes: PreparedNode[]; links: PreparedLink[]; linksByNode: Map<number, number[]> }>({ nodes: [], links: [], linksByNode: new Map() })
  const ambientSelectionRef = useRef<AmbientSelection>({ hubs: [] })
  const highlightedIndexesRef = useRef(new Set<number>())
  const displayOptionsRef = useRef({ showLabels, variant })
  const pointerRef = useRef({ down: false, moved: false, x: 0, y: 0 })
  const dimensionsRef = useRef({ width: 900, height: 620, dpr: 1 })
  const fittedRef = useRef(false)
  const stateRef = useRef({
    panX: 0,
    panY: 0,
    targetPanX: 0,
    targetPanY: 0,
    zoom: 0.5,
    targetZoom: 0.5,
    mouseX: -1,
    mouseY: -1,
    hoverIndex: -1,
    dragging: false,
  })

  const visibleData = useMemo(() => {
    const nodes = maxNodes ? data.nodes.slice(0, maxNodes) : data.nodes
    const ids = new Set(nodes.map((node) => node.id))
    return {
      nodes,
      links: data.links.filter((link) => (
        ids.has(link.source) && ids.has(link.target) && visibleLinkTypes.has(link.type)
      )),
    }
  }, [data, maxNodes, visibleLinkTypes])

  const prepared = useMemo(() => {
    const nodeIndexes = new Map(visibleData.nodes.map((node, index) => [node.id, index]))
    const degrees = new Map<string, number>()
    visibleData.links.forEach((link) => {
      degrees.set(link.source, (degrees.get(link.source) || 0) + 1)
      degrees.set(link.target, (degrees.get(link.target) || 0) + 1)
    })
    const timestamps = visibleData.nodes
      .map((node) => timestampValue(node.row, colorField))
      .filter((value): value is number => value !== null)
    const oldest = timestamps.length > 0 ? Math.min(...timestamps) : 0
    const newest = timestamps.length > 0 ? Math.max(...timestamps) : 0
    const count = Math.max(visibleData.nodes.length, 1)
    const baseRadius = Math.sqrt(count) * (variant === 'constellation' ? 30 : 38)

    const nodes: PreparedNode[] = visibleData.nodes.map((node, index) => {
      const seed = hashString(node.id) | 0
      let position = positionsRef.current.get(node.id)
      if (!position) {
        const angle = (index / count) * Math.PI * 2 + ((seed % 100) / 100) * 0.5
        const radius = baseRadius * 0.3 + ((Math.abs(seed) % 1_000) / 1_000) * baseRadius * 0.7
        position = {
          wx: Math.cos(angle) * radius + ((seed % 200) - 100) * 0.5,
          wy: Math.sin(angle) * radius + (((seed >> 8) % 200) - 100) * 0.5,
        }
        positionsRef.current.set(node.id, position)
      }
      const timestamp = timestampValue(node.row, colorField)
      return {
        ...node,
        ...position,
        degree: degrees.get(node.id) || 0,
        heat: timestamp === null || oldest === newest ? 0.5 : (timestamp - oldest) / (newest - oldest),
      }
    })

    const links: PreparedLink[] = []
    const linksByNode = new Map<number, number[]>()
    visibleData.links.forEach((link) => {
      const source = nodeIndexes.get(link.source)
      const target = nodeIndexes.get(link.target)
      if (source === undefined || target === undefined) return
      const linkIndex = links.length
      links.push({ source, target, type: link.type })
      if (linksByNode.has(source)) linksByNode.get(source)?.push(linkIndex)
      else linksByNode.set(source, [linkIndex])
      if (linksByNode.has(target)) linksByNode.get(target)?.push(linkIndex)
      else linksByNode.set(target, [linkIndex])
    })

    return { nodes, links, linksByNode }
  }, [colorField, variant, visibleData])

  const highlightedIndexes = useMemo(() => {
    const ids = new Set(highlightedIds)
    return new Set(prepared.nodes.map((node, index) => ids.has(node.id) ? index : -1).filter((index) => index >= 0))
  }, [highlightedIds, prepared.nodes])

  preparedRef.current = prepared
  ambientSelectionRef.current = {
    hubs: prepared.nodes
      .map((node, index) => ({ index, degree: node.degree }))
      .sort((left, right) => right.degree - left.degree)
      .slice(0, 8)
      .map(({ index }) => index),
  }
  highlightedIndexesRef.current = highlightedIndexes
  displayOptionsRef.current = { showLabels, variant }

  const requestDraw = useCallback(() => {
    if (frameRef.current !== null) return
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null
      drawRef.current()
    })
  }, [])

  useImperativeHandle(forwardedRef, () => ({
    imageFile: () => new Promise((resolve) => {
      const canvas = canvasRef.current
      if (!canvas) return resolve(null)
      canvas.toBlob((blob) => resolve(blob ? new File([blob], 'memorias.png', { type: 'image/png' }) : null), 'image/png')
    }),
  }), [])

  useEffect(() => {
    onSelectRef.current = onSelect
    onClearRef.current = onClear
  }, [onClear, onSelect])

  useEffect(() => {
    selectedIdRef.current = selectedId ?? null
    requestDraw()
  }, [requestDraw, selectedId])

  useEffect(() => {
    requestDraw()
  }, [highlightedIndexes, requestDraw])

  useEffect(() => {
    fittedRef.current = false
    requestDraw()
  }, [prepared, requestDraw])

  useEffect(() => {
    const canvas = canvasRef.current
    const ambientCanvas = ambientCanvasRef.current
    if (!canvas || !ambientCanvas) return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let palette = resolveCanvasPalette(canvas)

    const draw = () => {
      const context = canvas.getContext('2d')
      if (!context) return
      const prepared = preparedRef.current
      const highlightedIndexes = highlightedIndexesRef.current
      const { showLabels, variant } = displayOptionsRef.current
      const dimensions = dimensionsRef.current
      const state = stateRef.current
      const dark = document.documentElement.classList.contains('dark')

      if (!fittedRef.current && prepared.nodes.length > 0 && dimensions.width > 0 && dimensions.height > 0) {
        const radius = Math.max(...prepared.nodes.map((node) => Math.hypot(node.wx, node.wy)), 1)
        const fit = Math.min(dimensions.width, dimensions.height) / (radius * 2.5)
        state.zoom = fit
        state.targetZoom = fit
        state.panX = 0
        state.panY = 0
        state.targetPanX = 0
        state.targetPanY = 0
        fittedRef.current = true
      }

      state.panX = lerp(state.panX, state.targetPanX)
      state.panY = lerp(state.panY, state.targetPanY)
      state.zoom = lerp(state.zoom, state.targetZoom)

      const centerX = dimensions.width / 2 + state.panX
      const centerY = dimensions.height / 2 + state.panY
      const screenX = new Float32Array(prepared.nodes.length)
      const screenY = new Float32Array(prepared.nodes.length)
      const visible = new Uint8Array(prepared.nodes.length)

      prepared.nodes.forEach((node, index) => {
        const x = centerX + node.wx * state.zoom
        const y = centerY + node.wy * state.zoom
        screenX[index] = x
        screenY[index] = y
        visible[index] = x > -VIEW_MARGIN && x < dimensions.width + VIEW_MARGIN && y > -VIEW_MARGIN && y < dimensions.height + VIEW_MARGIN ? 1 : 0
      })

      if (state.mouseX >= 0 && !state.dragging) {
        let nearestDistance = state.zoom > 1.5 ? 80 : 30
        let nearestIndex = -1
        prepared.nodes.forEach((_node, index) => {
          if (!visible[index]) return
          const distance = Math.hypot(state.mouseX - screenX[index], state.mouseY - screenY[index])
          if (distance < nearestDistance) {
            nearestDistance = distance
            nearestIndex = index
          }
        })
        state.hoverIndex = nearestIndex
        canvas.style.cursor = nearestIndex >= 0 ? 'pointer' : 'default'
      }

      const selectedIndex = selectedIdRef.current
        ? prepared.nodes.findIndex((node) => node.id === selectedIdRef.current)
        : -1
      const activeIndex = state.hoverIndex >= 0 ? state.hoverIndex : selectedIndex
      const activeLinks = new Set<number>()
      highlightedIndexes.forEach((index) => {
        prepared.linksByNode.get(index)?.forEach((linkIndex) => activeLinks.add(linkIndex))
      })
      if (activeIndex >= 0) prepared.linksByNode.get(activeIndex)?.forEach((linkIndex) => activeLinks.add(linkIndex))
      const connectedNodes = new Set<number>()
      activeLinks.forEach((linkIndex) => {
        const link = prepared.links[linkIndex]
        connectedNodes.add(link.source)
        connectedNodes.add(link.target)
      })
      const showHighlightedLabels = highlightedIndexes.size <= 12

      context.setTransform(dimensions.dpr, 0, 0, dimensions.dpr, 0, 0)
      context.clearRect(0, 0, dimensions.width, dimensions.height)
      context.fillStyle = dark ? '#09090b' : '#ffffff'
      context.fillRect(0, 0, dimensions.width, dimensions.height)

      const drawLink = (link: PreparedLink, highlighted: boolean) => {
        const ax = screenX[link.source]
        const ay = screenY[link.source]
        const bx = screenX[link.target]
        const by = screenY[link.target]
        if ((ax < -VIEW_MARGIN && bx < -VIEW_MARGIN) || (ax > dimensions.width + VIEW_MARGIN && bx > dimensions.width + VIEW_MARGIN) || (ay < -VIEW_MARGIN && by < -VIEW_MARGIN) || (ay > dimensions.height + VIEW_MARGIN && by > dimensions.height + VIEW_MARGIN)) return false
        context.beginPath()
        context.moveTo(ax, ay)
        context.quadraticCurveTo((ax + bx) / 2 + (by - ay) * 0.08, (ay + by) / 2 - (bx - ax) * 0.08, bx, by)
        context.strokeStyle = palette.links[link.type]
        context.globalAlpha = highlighted ? 0.58 : 0.055 + Math.min(state.zoom * 0.035, 0.085)
        context.lineWidth = highlighted ? 1.3 : 0.4
        context.stroke()
        return true
      }

      let linksDrawn = 0
      for (const link of prepared.links) {
        if (linksDrawn >= MAX_DRAWN_LINKS) break
        if (drawLink(link, false)) linksDrawn += 1
      }
      let highlightedLinksDrawn = 0
      for (const linkIndex of activeLinks) {
        if (highlightedLinksDrawn >= MAX_HIGHLIGHTED_LINKS) break
        if (drawLink(prepared.links[linkIndex], true)) highlightedLinksDrawn += 1
      }

      context.globalAlpha = 1

      const labelGrid = new Set<string>()
      const canPlace = (x: number, y: number, width: number, height: number) => {
        const left = Math.floor(x / 90)
        const right = Math.floor((x + width) / 90)
        const top = Math.floor(y / 90)
        const bottom = Math.floor((y + height) / 90)
        for (let column = left; column <= right; column += 1) {
          for (let row = top; row <= bottom; row += 1) {
            if (labelGrid.has(`${column},${row}`)) return false
          }
        }
        for (let column = left; column <= right; column += 1) {
          for (let row = top; row <= bottom; row += 1) labelGrid.add(`${column},${row}`)
        }
        return true
      }

      const order = prepared.nodes
        .map((_node, index) => index)
        .filter((index) => visible[index] && index !== activeIndex)
        .sort((left, right) => Number(highlightedIndexes.has(left)) - Number(highlightedIndexes.has(right)))
      if (activeIndex >= 0 && visible[activeIndex]) order.push(activeIndex)

      order.forEach((index) => {
        const node = prepared.nodes[index]
        const hovered = index === activeIndex
        const highlighted = highlightedIndexes.has(index)
        const neighbor = connectedNodes.has(index)
        const hasFocus = activeIndex >= 0 || highlightedIndexes.size > 0
        const radius = Math.max(1.5, (2.5 + Math.min(node.degree * 0.15, 2.5)) * Math.min(state.zoom, 2))
        const nodeColor = variant === 'constellation' ? heatColor(node.heat) : node.color
        const displayColor = highlighted ? palette.focus : nodeColor || palette.focus
        context.beginPath()
        context.arc(screenX[index], screenY[index], radius, 0, Math.PI * 2)
        context.fillStyle = displayColor
        context.globalAlpha = hovered || highlighted ? 1 : neighbor ? 0.9 : hasFocus ? 0.24 : 0.45 + Math.min(node.degree * 0.03, 0.5)
        if (hovered || highlighted || neighbor) {
          context.shadowColor = highlighted ? palette.focus : nodeColor || palette.focus
          context.shadowBlur = hovered || highlighted ? 22 : 10
        }
        context.fill()
        context.shadowBlur = 0
        context.globalAlpha = 1

        if (highlighted) {
          context.beginPath()
          context.arc(screenX[index], screenY[index], radius * 2.65, 0, Math.PI * 2)
          context.strokeStyle = palette.focus
          context.lineWidth = 1.5
          context.globalAlpha = 0.44
          context.stroke()
          context.globalAlpha = 1
        } else if (node.degree > 3 && !hovered && activeIndex < 0) {
          context.beginPath()
          context.arc(screenX[index], screenY[index], radius * 2, 0, Math.PI * 2)
          context.fillStyle = nodeColor
          context.globalAlpha = 0.06 + Math.min(node.degree * 0.005, 0.08)
          context.fill()
          context.globalAlpha = 1
        }

        const labelAllowed = hovered || highlighted && showHighlightedLabels || (showLabels && (state.zoom > LABEL_ZOOM_THRESHOLD || neighbor))
        if (!labelAllowed) return
        const label = node.label.length > LABEL_MAX_CHARS ? `${node.label.slice(0, LABEL_MAX_CHARS)}…` : node.label
        context.font = `${hovered || highlighted ? 600 : 500} 11px ui-sans-serif, system-ui, sans-serif`
        const width = Math.min(context.measureText(label).width + 14, LABEL_MAX_WIDTH)
        const height = 24
        const x = screenX[index] + radius + 5
        const y = screenY[index] - height / 2
        if (!hovered && !canPlace(x, y, width, height)) return
        context.fillStyle = dark ? 'rgba(24,24,27,.94)' : 'rgba(255,255,255,.94)'
        context.strokeStyle = dark ? '#3f3f46' : '#e4e4e7'
        context.lineWidth = 1
        context.beginPath()
        context.roundRect(x, y, width, height, 6)
        context.fill()
        context.stroke()
        context.fillStyle = dark ? '#e4e4e7' : '#3f3f46'
        context.textAlign = 'left'
        context.textBaseline = 'middle'
        context.save()
        context.beginPath()
        context.rect(x + 7, y, width - 14, height)
        context.clip()
        context.fillText(label, x + 7, y + height / 2)
        context.restore()
      })

      const moving = Math.abs(state.panX - state.targetPanX) > 0.15 || Math.abs(state.panY - state.targetPanY) > 0.15 || Math.abs(state.zoom - state.targetZoom) > 0.002
      if (moving) requestDraw()
    }
    drawRef.current = draw

    const drawAmbient = (timestamp: number) => {
      ambientFrameRef.current = requestAnimationFrame(drawAmbient)
      if (document.hidden) return

      const context = ambientCanvas.getContext('2d')
      if (!context) return
      const prepared = preparedRef.current
      const selection = ambientSelectionRef.current
      const dimensions = dimensionsRef.current
      const state = stateRef.current
      const dark = document.documentElement.classList.contains('dark')
      const centerX = dimensions.width / 2 + state.panX
      const centerY = dimensions.height / 2 + state.panY
      const screenPoint = (index: number) => {
        const node = prepared.nodes[index]
        return node ? { x: centerX + node.wx * state.zoom, y: centerY + node.wy * state.zoom } : null
      }

      context.setTransform(dimensions.dpr, 0, 0, dimensions.dpr, 0, 0)
      context.clearRect(0, 0, dimensions.width, dimensions.height)
      context.globalCompositeOperation = dark ? 'screen' : 'source-over'

      const focusedIndexes = Array.from(highlightedIndexesRef.current).slice(0, 28)
      if (state.hoverIndex >= 0 && !focusedIndexes.includes(state.hoverIndex)) focusedIndexes.push(state.hoverIndex)
      const synapseSources = focusedIndexes.length > 0 ? focusedIndexes : selection.hubs.slice(0, 2)
      const synapseLinks = new Set<number>()
      synapseSources.forEach((nodeIndex) => {
        prepared.linksByNode.get(nodeIndex)?.forEach((linkIndex) => {
          if (synapseLinks.size < 60) synapseLinks.add(linkIndex)
        })
      })
      const focused = focusedIndexes.length > 0
      let synapseIndex = 0
      synapseLinks.forEach((linkIndex) => {
        const link = prepared.links[linkIndex]
        const source = link ? screenPoint(link.source) : null
        const target = link ? screenPoint(link.target) : null
        if (!source || !target) return
        if ((source.x < -40 && target.x < -40) || (source.x > dimensions.width + 40 && target.x > dimensions.width + 40) || (source.y < -40 && target.y < -40) || (source.y > dimensions.height + 40 && target.y > dimensions.height + 40)) return
        const pulse = Math.sin(timestamp / 390 + synapseIndex * 0.63) * 0.5 + 0.5
        context.beginPath()
        context.moveTo(source.x, source.y)
        context.quadraticCurveTo((source.x + target.x) / 2 + (target.y - source.y) * 0.08, (source.y + target.y) / 2 - (target.x - source.x) * 0.08, target.x, target.y)
        const activeLinkColor = palette.links[link.type]
        context.strokeStyle = focused ? activeLinkColor : palette.focus
        context.lineWidth = focused ? 0.55 + pulse * 0.55 : 0.4 + pulse * 0.28
        context.globalAlpha = focused ? 0.1 + pulse * 0.24 : 0.02 + pulse * 0.055
        context.shadowColor = focused ? activeLinkColor : palette.focus
        context.shadowBlur = focused ? 3 + pulse * 6 : 2
        context.stroke()
        synapseIndex += 1
      })
      context.shadowBlur = 0

      selection.hubs.forEach((nodeIndex, index) => {
        const point = screenPoint(nodeIndex)
        if (!point || point.x < -30 || point.x > dimensions.width + 30 || point.y < -30 || point.y > dimensions.height + 30) return
        const wave = (timestamp / 2_800 + index * 0.19) % 1
        context.beginPath()
        context.arc(point.x, point.y, 5 + wave * 16, 0, Math.PI * 2)
        context.strokeStyle = palette.focus
        context.lineWidth = 0.8
        context.globalAlpha = (1 - wave) * 0.18
        context.stroke()
      })

      focusedIndexes.forEach((nodeIndex, index) => {
        const point = screenPoint(nodeIndex)
        const node = prepared.nodes[nodeIndex]
        if (!point || !node || point.x < -40 || point.x > dimensions.width + 40 || point.y < -40 || point.y > dimensions.height + 40) return
        const baseRadius = Math.max(3.5, (2.5 + Math.min(node.degree * 0.15, 2.5)) * Math.min(state.zoom, 2))

        for (let ring = 0; ring < 2; ring += 1) {
          const phase = (timestamp / 1_360 + ring * 0.5 + index * 0.025) % 1
          context.beginPath()
          context.arc(point.x, point.y, baseRadius * (1.8 + phase * 3.4), 0, Math.PI * 2)
          context.strokeStyle = palette.focus
          context.lineWidth = 1.1
          context.globalAlpha = (1 - phase) * 0.52
          context.shadowColor = palette.focus
          context.shadowBlur = 8
          context.stroke()
        }

        const corePulse = Math.sin(timestamp / 230 + index) * 0.5 + 0.5
        context.beginPath()
        context.arc(point.x, point.y, baseRadius * (1.25 + corePulse * 0.28), 0, Math.PI * 2)
        context.fillStyle = palette.focus
        context.globalAlpha = 0.08 + corePulse * 0.12
        context.fill()
      })

      context.shadowBlur = 0
      context.globalAlpha = 1
      context.globalCompositeOperation = 'source-over'
    }
    if (!reduceMotion) ambientFrameRef.current = requestAnimationFrame(drawAmbient)

    const resize = () => {
      const bounds = canvas.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const pixelWidth = Math.max(Math.floor(bounds.width * dpr), 1)
      const pixelHeight = Math.max(Math.floor(bounds.height * dpr), 1)
      const sizeChanged = canvas.width !== pixelWidth || canvas.height !== pixelHeight
      dimensionsRef.current = { width: bounds.width, height: bounds.height, dpr }
      if (sizeChanged) {
        canvas.width = pixelWidth
        canvas.height = pixelHeight
        fittedRef.current = false
      }
      if (ambientCanvas.width !== pixelWidth || ambientCanvas.height !== pixelHeight) {
        ambientCanvas.width = pixelWidth
        ambientCanvas.height = pixelHeight
      }
      requestDraw()
    }
    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    resize()

    const point = (event: PointerEvent | WheelEvent) => {
      const bounds = canvas.getBoundingClientRect()
      return { x: event.clientX - bounds.left, y: event.clientY - bounds.top }
    }
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault()
      const factor = event.deltaY > 0 ? 0.9 : 1.1
      stateRef.current.targetZoom = Math.max(0.03, Math.min(8, stateRef.current.targetZoom * factor))
      requestDraw()
    }
    const handlePointerDown = (event: PointerEvent) => {
      pointerRef.current = { down: true, moved: false, x: event.clientX, y: event.clientY }
      stateRef.current.dragging = true
      canvas.setPointerCapture(event.pointerId)
      canvas.style.cursor = 'grabbing'
    }
    const handlePointerMove = (event: PointerEvent) => {
      const position = point(event)
      stateRef.current.mouseX = position.x
      stateRef.current.mouseY = position.y
      const pointer = pointerRef.current
      if (pointer.down) {
        const dx = event.clientX - pointer.x
        const dy = event.clientY - pointer.y
        if (Math.abs(dx) + Math.abs(dy) > 2) pointer.moved = true
        stateRef.current.targetPanX += dx
        stateRef.current.targetPanY += dy
        pointer.x = event.clientX
        pointer.y = event.clientY
      }
      requestDraw()
    }
    const handlePointerUp = (event: PointerEvent) => {
      const pointer = pointerRef.current
      pointer.down = false
      stateRef.current.dragging = false
      if (!pointer.moved && stateRef.current.hoverIndex >= 0) {
        const node = preparedRef.current.nodes[stateRef.current.hoverIndex]
        if (node?.row) onSelectRef.current(node.row)
      } else if (!pointer.moved) {
        onClearRef.current()
      }
      canvas.releasePointerCapture(event.pointerId)
      requestDraw()
    }
    const handlePointerLeave = () => {
      pointerRef.current.down = false
      stateRef.current.dragging = false
      stateRef.current.mouseX = -1
      stateRef.current.mouseY = -1
      stateRef.current.hoverIndex = -1
      canvas.style.cursor = 'default'
      requestDraw()
    }

    canvas.addEventListener('wheel', handleWheel, { passive: false })
    canvas.addEventListener('pointerdown', handlePointerDown)
    canvas.addEventListener('pointermove', handlePointerMove)
    canvas.addEventListener('pointerup', handlePointerUp)
    canvas.addEventListener('pointerleave', handlePointerLeave)
    const themeObserver = new MutationObserver(() => {
      palette = resolveCanvasPalette(canvas)
      requestDraw()
    })
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

    return () => {
      observer.disconnect()
      themeObserver.disconnect()
      canvas.removeEventListener('wheel', handleWheel)
      canvas.removeEventListener('pointerdown', handlePointerDown)
      canvas.removeEventListener('pointermove', handlePointerMove)
      canvas.removeEventListener('pointerup', handlePointerUp)
      canvas.removeEventListener('pointerleave', handlePointerLeave)
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      if (ambientFrameRef.current !== null) cancelAnimationFrame(ambientFrameRef.current)
      frameRef.current = null
      ambientFrameRef.current = null
    }
  }, [requestDraw])

  return (
    <div className="relative h-full w-full">
      <canvas ref={canvasRef} role="img" aria-label={ariaLabel} className="absolute inset-0 h-full w-full touch-none select-none" />
      <canvas ref={ambientCanvasRef} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full" />
    </div>
  )
})

export default MemoryNetworkCanvas
